import dayjs from "dayjs";
import firebase from "../../configs/FirebaseConfig";
import {
  FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE,
  FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE,
  FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE,
} from "../../constants/FirestoreConstant";
import { getInvoiceDeletionBlockers } from "../invoiceDeletion";
import { computeFinalInvoiceTotals, sumProformaPaid } from "../proformaLinks";
import { sumOwnPayments } from "./proformaLinks";

const firestore = firebase.firestore();
const invoicesCol = () => firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE);

/**
 * Verwijderen van een inkoopfactuur. Of het mag, bepaalt getInvoiceDeletionBlockers
 * (../invoiceDeletion.js). Hier gebeurt het opruimen in één transactie:
 * - definitieve factuur: gekoppelde pro forma's los (terug naar "niet verrekend", hun betalingen
 *   blijven op de pro forma), en de zending wijst niet meer naar de factuur;
 * - pro forma: uit de lijst van elke definitieve factuur die hem meerekent, met de totalen van
 *   die factuur in dezelfde transactie opnieuw berekend (niet achteraf terug-lezen, zie
 *   recomputeFinalInvoiceTotals in ./proformaLinks.js);
 * - een kopie van de factuur in activityLogs, want de geschiedenis-subcollectie blijft onzichtbaar
 *   achter (de regels staan verwijderen van geschiedenis niet toe). De PDF blijft in Storage staan.
 */

export class InvoiceDeletionBlockedError extends Error {
  constructor(blockers) {
    super(blockers.join(" "));
    this.name = "InvoiceDeletionBlockedError";
    this.blockers = blockers;
  }
}

export const deleteInvoice = async ({ invoiceId, email, uid = null }) => {
  const invoiceRef = invoicesCol().doc(invoiceId);
  const before = await invoiceRef.get();
  if (!before.exists) throw new InvoiceDeletionBlockedError(["De factuur bestaat niet meer."]);
  const { supplierId, type, linkedFinalInvoiceId } = before.data();

  // Queries kunnen niet in een transactie; wat ze vinden wordt daarin opnieuw gelezen.
  const paymentsSnap = await firestore
    .collection(FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE)
    .where("invoiceId", "==", invoiceId)
    .get();
  const paymentCount = paymentsSnap.size;
  // Bij een pro forma: elke definitieve factuur die hem in zijn lijst heeft, ook een achterhaalde
  // claim (zie functions/proformaLinks.js), anders blijft er een verwijzing naar niets achter.
  const claimingFinalIds =
    type === "proforma" && supplierId
      ? (await invoicesCol().where("supplierId", "==", supplierId).get()).docs
          .filter((d) => d.data().type === "final" && (d.data().linkedProformaInvoiceIds || []).includes(invoiceId))
          .map((d) => d.id)
      : [];
  const ownPaidById = await sumOwnPayments([...claimingFinalIds, ...(linkedFinalInvoiceId ? [linkedFinalInvoiceId] : [])]);

  let releasedFinalIds = [];
  let releasedProformaIds = [];
  await firestore.runTransaction(async (tx) => {
    // Eerst alle reads, dan pas writes.
    const snap = await tx.get(invoiceRef);
    if (!snap.exists) throw new InvoiceDeletionBlockedError(["De factuur bestaat niet meer."]);
    const invoice = { id: snap.id, ...snap.data() };
    const isProforma = invoice.type === "proforma";

    const shipmentRef =
      !isProforma && invoice.shipmentId
        ? firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).doc(invoice.shipmentId)
        : null;
    const shipmentSnap = shipmentRef ? await tx.get(shipmentRef) : null;
    const shipment = shipmentSnap?.exists ? { id: shipmentSnap.id, ...shipmentSnap.data() } : null;

    const proformaSnaps = isProforma
      ? []
      : await Promise.all((invoice.linkedProformaInvoiceIds || []).map((id) => tx.get(invoicesCol().doc(id))));

    const finalIds = isProforma
      ? [...new Set([...claimingFinalIds, ...(invoice.linkedFinalInvoiceId ? [invoice.linkedFinalInvoiceId] : [])])]
      : [];
    const finalSnaps = await Promise.all(finalIds.map((id) => tx.get(invoicesCol().doc(id))));
    const finals = finalSnaps.filter((s) => s.exists).map((s) => ({ id: s.id, ...s.data() }));
    const linkedFinalInvoice = finals.find((f) => f.id === invoice.linkedFinalInvoiceId) || null;
    const linkedFinalShipmentSnap = linkedFinalInvoice?.shipmentId
      ? await tx.get(firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).doc(linkedFinalInvoice.shipmentId))
      : null;
    const linkedFinalShipment = linkedFinalShipmentSnap?.exists
      ? { id: linkedFinalShipmentSnap.id, ...linkedFinalShipmentSnap.data() }
      : null;

    // De pro forma's die bij de losgelaten definitieve facturen blijven hangen, voor hun totalen.
    const remainingIds = [
      ...new Set(finals.flatMap((f) => (f.linkedProformaInvoiceIds || []).filter((id) => id !== invoice.id))),
    ];
    const remainingSnaps = await Promise.all(remainingIds.map((id) => tx.get(invoicesCol().doc(id))));
    const remainingById = new Map(remainingSnaps.filter((s) => s.exists).map((s) => [s.id, s.data()]));

    const blockers = getInvoiceDeletionBlockers({
      invoice,
      paymentCount,
      shipment,
      linkedFinalInvoice,
      linkedFinalShipment,
    });
    if (blockers.length > 0) throw new InvoiceDeletionBlockedError(blockers);

    const now = dayjs().unix();
    const label = invoice.invoiceNumber || invoice.id;
    const history = (ref, change) =>
      tx.set(ref.collection("history").doc(), { changedBy: email, changedAt: now, changes: [change] });

    // Bij een retry van de transactie beginnen we met schone lijsten.
    const attemptProformas = [];
    const attemptFinals = [];

    proformaSnaps
      .filter((s) => s.exists && s.data().linkedFinalInvoiceId === invoice.id)
      .forEach((s) => {
        tx.update(s.ref, { linkedFinalInvoiceId: null, linkedAt: null, updatedAt: now, updatedBy: email });
        history(s.ref, {
          field: "proforma_unlinked",
          oldValue: invoice.id,
          newValue: null,
          action: "invoice_deleted",
          metadata: `- Ontkoppeld: definitieve factuur ${label} is verwijderd`,
        });
        attemptProformas.push(s.id);
      });

    if (shipment && shipment.invoiceId === invoice.id) {
      tx.update(shipmentRef, { invoiceId: null, updatedAt: now, updatedBy: email });
    }

    finals
      .filter((f) => (f.linkedProformaInvoiceIds || []).includes(invoice.id))
      .forEach((f) => {
        const rest = f.linkedProformaInvoiceIds.filter((id) => id !== invoice.id);
        const ref = invoicesCol().doc(f.id);
        const totals = computeFinalInvoiceTotals({
          totalAmount: f.totalAmount || 0,
          ownPaid: ownPaidById.get(f.id) || 0,
          linkedPaid: sumProformaPaid(rest, remainingById),
        });
        tx.update(ref, {
          linkedProformaInvoiceIds: rest.length > 0 ? rest : null,
          ...totals,
          updatedAt: now,
          updatedBy: email,
        });
        history(ref, {
          field: "proforma_unlinked",
          oldValue: invoice.id,
          newValue: null,
          action: "invoice_deleted",
          metadata: `- Proforma verwijderd: ${label}`,
        });
        attemptFinals.push(f.id);
      });

    tx.set(firestore.collection("activityLogs").doc(), {
      verb: "invoice_deleted",
      targetType: "purchaseOrderInvoice",
      targetId: invoice.id,
      targetLabel: label,
      actorEmail: email,
      actorUid: uid,
      ts: firebase.firestore.FieldValue.serverTimestamp(),
      // De hele factuur zoals hij was, zodat hij terug te vinden (en zo nodig te herstellen) is.
      snapshot: invoice,
      releasedProformaIds: attemptProformas,
      releasedFinalInvoiceIds: attemptFinals,
      shipmentIdCleared: shipment && shipment.invoiceId === invoice.id ? shipment.id : null,
    });

    tx.delete(invoiceRef);
    releasedProformaIds = attemptProformas;
    releasedFinalIds = attemptFinals;
  });

  return { releasedProformaIds, releasedFinalIds };
};
